import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { environment } from '../../environments/environment';
import { User } from '../shared/models';

declare global {
	interface Window {
		dataLayer: any[];
		gtag?: (...args: any[]) => void;
	}
}

declare const pkDataLayer: any;

export interface DataLayerEvent {
	event: string;
	[key: string]: any;
}

@Injectable({
	providedIn: 'root',
})
export class DataLayerService {
	private isInitialized = false;
	private eventQueue: DataLayerEvent[] = [];

	constructor(@Inject(PLATFORM_ID) private platformId: object) {
		this.initializeDataLayer();
	}

	private initializeDataLayer(): void {
		// Initialize pkDataLayer if it doesn't exist (fallback to dataLayer)
		if (!pkDataLayer) {
			if (!window.dataLayer) {
				window.dataLayer = [];
			}
		}

		this.isInitialized = true;
		this.flushEventQueue();
	}

	private flushEventQueue(): void {
		while (this.eventQueue.length > 0) {
			const event = this.eventQueue.shift();
			if (event) {
				this.pushEvent(event);
			}
		}
	}

	/**
	 * Push an event to the data layer
	 * @param event - Event name
	 * @param data - Additional data to send with the event
	 */
	pushToDataLayer(event: string, data: { [key: string]: any } = {}): void {
		const eventData: DataLayerEvent = { event, ...data };

		if (!this.isInitialized) {
			this.eventQueue.push(eventData);
			return;
		}

		this.pushEvent(eventData);
	}

	private pushEvent(eventData: DataLayerEvent): void {
		try {
			// Use pkDataLayer if available (for GTM), otherwise fallback to dataLayer
			if (pkDataLayer) {
				pkDataLayer.push(eventData);
				console.debug('pkDataLayer event pushed:', eventData);
			} else if (window.dataLayer) {
				window.dataLayer.push(eventData);
				console.debug('dataLayer event pushed:', eventData);
			} else {
				console.warn('No data layer available, queuing event:', eventData);
				this.eventQueue.push(eventData);
			}
		} catch (error) {
			console.error('Error pushing to data layer:', error, eventData);
		}
	}

	/**
	 * Push an event to the data layer with environment checks (enhanced version)
	 */
	pushToDataLayerWithEnvironmentCheck(event: string, data: { [key: string]: any } = {}): void {
		// Only push in production and non-demo environments
		if (environment.production && !environment.demo) {
			this.pushToDataLayer(event, data);
		}
	}

	/**
	 * Push a page view event
	 */
	pushPageView(pagePath: string, pageTitle?: string): void {
		this.pushToDataLayer('page_view', {
			page_path: pagePath,
			page_title: pageTitle || document.title,
		});
	}

	/**
	 * Push an app error event for GTM (empty states, API failures, no results, etc.)
	 */
	pushAppError(
		errorType: 'empty_state' | 'api_error' | 'no_results',
		pageLocation?: string,
		extra: { [key: string]: any } = {},
	): void {
		this.pushToDataLayer('app_error', {
			errorType,
			pageLocation: pageLocation ?? window.location.pathname,
			...extra,
		});
	}

	/**
	 * Push a custom event with structured data
	 */
	pushCustomEvent(eventName: string, parameters: { [key: string]: any } = {}): void {
		this.pushToDataLayer(eventName, {
			event_category: 'custom',
			...parameters,
		});
	}

	/**
	 * Push an e-commerce event
	 */
	pushEcommerceEvent(eventName: string, ecommerceData: { [key: string]: any }): void {
		this.pushToDataLayer(eventName, {
			ecommerce: ecommerceData,
		});
	}

	/**
	 * Clear the data layer (useful for testing)
	 */
	clearDataLayer(): void {
		if (pkDataLayer) {
			pkDataLayer.length = 0;
		}
		if (window.dataLayer) {
			window.dataLayer.length = 0;
		}
	}

	/**
	 * Get current data layer contents (useful for debugging)
	 */
	getDataLayer(): any[] {
		if (pkDataLayer) {
			return [...pkDataLayer];
		}
		return [...window.dataLayer];
	}

	/**
	 * Send page event with user context (from GoogleTagManagerService)
	 */
	sendPageEvent(action: string, user: User, title: string): void {
		if (user && user.agentId) {
			this.fireTagEvent({
				event: title,
				'agent-id': user.agentId,
				action,
			});
		}
	}

	/**
	 * Fire tag event with environment checks (from GoogleTagManagerService)
	 */
	fireTagEvent(args: { event: string; 'agent-id'?: string; value?: string; action?: string }): void {
		if (pkDataLayer && environment.production && !environment.demo) {
			pkDataLayer.push(args);
		}
	}
}
